import { UserIcon } from 'assets/icons';
import { format } from 'date-fns';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { FormControl } from 'pages/ModerationRequest/redesign/FormControl';
import { FormWrapper } from 'pages/ModerationRequest/redesign/FormWrapper';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { moderationRequestActions } from 'reduxStore/reducers/ModerationRequestSlice';
import { getModerationRequestData } from 'reduxStore/reducers/ModerationRequestSlice/selectors/getModerationRequestData';
import { getModerationRequestStatus } from 'reduxStore/reducers/ModerationRequestSlice/selectors/getModerationRequestStatus';
import { getFtsProfile } from 'reduxStore/reducers/ProfileSlice/selectors/getFtsProfile';
import { getProfile } from 'reduxStore/reducers/ProfileSlice/selectors/getProfile';
import { Step1Fields } from './Step1Fields';
import { checkInn, checkName, checkSnils, compareNames } from './consts';
import { Step1Payload } from './types';
import { RequestHandler } from '../finalStep/consts';
import { profileActions } from 'reduxStore/reducers/ProfileSlice';

export const Step1 = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isFormDisabled = useAppSelector(getModerationRequestStatus);
  const {
    lastName: profileLastName,
    firstName: profileFirstName,
    middleName: profileMiddleName,
    inn: profileInn,
    created,
  } = useAppSelector(getProfile);
  const {
    inn: moderationRequestInn,
    status,
    birthDate,
    lastName: requestLastName,
    firstName: requestFirstName,
    middleName: requestMiddleName,
    snils,
  } = useAppSelector(getModerationRequestData);
  const ftsProfile = useAppSelector(getFtsProfile);

  const lastName =
    requestLastName === null || requestLastName === undefined ? profileLastName : requestLastName;
  const firstName =
    requestFirstName === null || requestFirstName === undefined
      ? profileFirstName
      : requestFirstName;
  const middleName =
    requestMiddleName === null || requestMiddleName === undefined
      ? profileMiddleName
      : requestMiddleName;

  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError,
    watch,
  } = useForm<Step1Payload>({
    defaultValues: {
      lastName,
      firstName,
      middleName,
      birthDate,
      inn: moderationRequestInn ? moderationRequestInn : profileInn,
      snils,
    },
    mode: 'onBlur',
  });

  const onSubmit = () => {
    handleSubmit((data) => {
      const trimmedData = {
        ...data,
        firstName: data.firstName ? data.firstName.trim() : data.firstName,
        lastName: data.lastName ? data.lastName.trim() : data.lastName,
        middleName: data.middleName ? data.middleName.trim() : data.middleName,
      };
      if (typeof trimmedData.birthDate === 'object') {
        trimmedData.birthDate =
          trimmedData.birthDate && format(trimmedData.birthDate, 'yyyy-MM-dd');
      }
      const toastErrors: string[] = [];
      checkName(trimmedData.firstName, 'firstName', 'Имя должно', toastErrors, setError);
      checkName(trimmedData.lastName, 'lastName', 'Фамилия должна', toastErrors, setError);
      if (trimmedData.middleName) {
        checkName(trimmedData.middleName, 'middleName', 'Отчество должно', toastErrors, setError);
      }
      if (trimmedData.snils) {
        checkSnils(trimmedData.snils, RequestHandler.DRAFT, toastErrors, setError);
      }
      checkInn(trimmedData.inn, toastErrors, setError);

      ftsProfile?.ftsLastName &&
        compareNames(
          trimmedData.lastName,
          ftsProfile?.ftsLastName,
          'lastName',
          'Фамилия',
          toastErrors,
          setError
        );
      ftsProfile?.ftsFirstName &&
        compareNames(
          trimmedData.firstName,
          ftsProfile?.ftsFirstName,
          'firstName',
          'Имя',
          toastErrors,
          setError
        );
      ftsProfile?.ftsMiddleName &&
        compareNames(
          trimmedData.middleName,
          ftsProfile?.ftsMiddleName,
          'middleName',
          'Отчество',
          toastErrors,
          setError
        );

      if (toastErrors.length) {
        const onlyUnique = new Set(toastErrors);
        Array.from(onlyUnique).forEach((message: string) => {
          toast.error(message);
        });
        return;
      }
      dispatch(moderationRequestActions.setPrivateData(trimmedData));
      trimmedData.inn !== null && dispatch(profileActions.setInn(trimmedData.inn));
      navigate(`/moderation-request/step2`);
    })();
  };

  useEffect(() => {
    const toastErrors: string[] = [];

    ftsProfile &&
      compareNames(lastName, ftsProfile.ftsLastName, 'lastName', 'Фамилия', toastErrors, setError);
    ftsProfile &&
      compareNames(firstName, ftsProfile.ftsFirstName, 'firstName', 'Имя', toastErrors, setError);
    ftsProfile &&
      compareNames(
        middleName,
        ftsProfile.ftsMiddleName,
        'middleName',
        'Отчество',
        toastErrors,
        setError
      );
  }, [watch]);

  return (
    <FormWrapper title="Личные данные" status={status} created={created} Icon={UserIcon}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex min-h-[65vh] flex-col justify-between"
      >
        <Step1Fields
          errors={errors}
          control={control}
          isFormDisabled={isFormDisabled}
          clearErrors={clearErrors}
        />
        <FormControl />
      </form>
    </FormWrapper>
  );
};
